import Vue from 'vue'
import { getProfileDetail, updateAccountProfileId, getContentReviewProfiles } from '../api'

const state = {
  activeProfile: {
    id: '',
    name: '',
    standard_sets: [],
    subjects: [],
    image_url: ''
  },
  _storedProfilesById: {},
  fetchingActiveProfile: false,
  updatingAccountProfile: false,
  isProfileSelectorOpen: false,
  contentReviewCustomization: null,
  contentReviewCriteria: null,
  fetchingContentReviewCustomization: false
}

export const mutations = {
  setActiveProfile(state, profile) {
    state.activeProfile = profile

    if (!state._storedProfilesById[profile.id]) {
      Vue.set(state._storedProfilesById, profile.id, profile)
    }
  },
  setFetchingActiveProfile(state, flag) {
    state.fetchingActiveProfile = flag
  },
  setUpdatingAccountProfile(state, flag) {
    state.updatingAccountProfile = flag
  },
  setIsProfileSelectorOpen(state, flag) {
    state.isProfileSelectorOpen = flag
  },
  storeProfile(state, profile) {
    if (profile && profile.id) {
      Vue.set(state._storedProfilesById, profile.id, profile)
    }
  },
  setContentReviewCustomization(state, criteriaSetName) {
    state.contentReviewCustomization = criteriaSetName
  },
  setContentReviewCriteria(state, criteria) {
    state.contentReviewCriteria = criteria
  },
  setFetchingContentReviewCustomization(state, flag) {
    state.fetchingContentReviewCustomization = flag
  }
}

export const actions = {
  async getProfileDetail({ commit, state }, profileId) {
    if (state._storedProfilesById[profileId]) {
      console.info(`Returning stored profile info for ${profileId}`)
      return state._storedProfilesById[profileId]
    } else {
      let profileInfo = await getProfileDetail(profileId)
      commit('storeProfile', profileInfo)
      return profileInfo
    }
  },
  async setActiveProfileById({ dispatch, commit }, profileId) {
    if (!profileId) {
      return
    }

    commit('setFetchingActiveProfile', true)
    let profileInfo = await dispatch('getProfileDetail', profileId)
    commit('setFetchingActiveProfile', false)
    commit('setActiveProfile', profileInfo)
    return profileInfo
  },
  async updateAccountProfileId({ commit }, { userUuid, profileId }) {
    if (!profileId) {
      return
    }

    commit('setUpdatingAccountProfile', true)
    try {
      await updateAccountProfileId({ userUuid, profileId })
    } catch (err) {
      console.error(`Could not save account with selected content profile: ${JSON.stringify(err)}`)
    }
    commit('setUpdatingAccountProfile', false)
    return
  },
  async fetchContentReviewCustomization({ commit, state, rootGetters }) {
    const profileId = state.activeProfile?.id
    if (!profileId) {
      return
    }

    commit('setFetchingContentReviewCustomization', true)
    try {
      const profiles = await getContentReviewProfiles()
      const betaVisibility = Boolean(rootGetters?.['visibility/betaVisibility'])
      const canUseCriteriaSet = (profile) => Boolean(profile?.released !== false || betaVisibility)

      const userCriteriaSet = profiles.find(p => p.content_profile === profileId && canUseCriteriaSet(p))
      if (userCriteriaSet) {
        commit('setContentReviewCustomization', userCriteriaSet.criteria_set_name || null)
        commit('setContentReviewCriteria', userCriteriaSet.criteria_by_type || null)
        return userCriteriaSet
      } else {
        // Fall back to default set (empty string content_profile)
        const defaultCriteriaSet = profiles.find(p => p.content_profile === '' && canUseCriteriaSet(p))
        if (defaultCriteriaSet) {
          commit('setContentReviewCustomization', defaultCriteriaSet.criteria_set_name || null)
          commit('setContentReviewCriteria', defaultCriteriaSet.criteria_by_type || null)
          return defaultCriteriaSet
        } else {
          commit('setContentReviewCustomization', null)
          commit('setContentReviewCriteria', null)
        }
      }
    } catch (error) {
      console.error('Error fetching content review profiles:', error)
      commit('setContentReviewCustomization', null)
      commit('setContentReviewCriteria', null)
    } finally {
      commit('setFetchingContentReviewCustomization', false)
    }
  }
}

export const getters = {
  profileId(state) {
    return (state.activeProfile && state.activeProfile.id) || ''
  },
  profileImageUrl(state) {
    return (state.activeProfile && state.activeProfile.image_url) || ''
  },
  visibleStandardSets(state) {
    return (state.activeProfile && state.activeProfile.standard_sets) || ''
  },
  visibleStandardSetsForSubject(state) {
    let subjects = state.activeProfile.subjects || []
    return (subject, { onlyPublished }) => {
      if (!subject) {
        return []
      }
      let subjectInfo = subjects.find(s => {
        if (onlyPublished) {
          return s.subject === subject && s.published
        }
        return s.subject === subject
      })

      return (subjectInfo && subjectInfo.standard_sets) || []
    }
  },
  keyStandardSetForSubject(state) {
    let subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.key_standard_set) || ''
    }
  },
  staircaseStandardSetForSubject(state) {
    let subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.staircase_standard_set) || ''
    }
  },
  dropdownStandardSetForSubject(state) {
    var subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.dropdown_standard_set) || ''
    }
  },
  publishedSubjects(state) {
    return (
      (state.activeProfile &&
        state.activeProfile.subjects &&
        state.activeProfile.subjects.filter(subject => subject.published)) ||
      []
    )
  },
  standardSetLabelForSubjectCard(state) {
    var subjects = state.activeProfile.subjects || []
    return subject => {
      if (!subject) {
        return ''
      }
      let subjectInfo = subjects.find(s => s.subject === subject)

      return (subjectInfo && subjectInfo.standard_set_label) || ''
    }
  },
  contentReviewCriteriaSetName(state) {
    return state.contentReviewCustomization
  },
  contentReviewCriteria(state) {
    return state.contentReviewCriteria
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
